import { useState } from 'react';
import { Button, Checkbox, Input, RichTextEditor, SectionCard, toast } from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import type { CollectionMall } from '../../productCollection/collectionMalls';
import { COMMON_CONSENT_LABELS } from '../commonConsents';
import { useSaveConsentTemplateMutation, type ConsentTemplate } from '../hooks/useConsentTemplates';

const TITLE_MAX = 200;

/** QA-507 — 공통 동의 1종·1몰 문구 편집(제목·본문·필수). 저장하면 사용함인 모든 상품의 그 몰 문구가 바뀐다. */
export const ConsentTemplateCard = ({
  template,
  mall,
  canEdit,
}: {
  template: ConsentTemplate;
  mall: CollectionMall;
  canEdit: boolean;
}) => {
  const [title, setTitle] = useState(template.title);
  const [contentHtml, setContentHtml] = useState(template.contentHtml);
  const [required, setRequired] = useState(template.required);
  const save = useSaveConsentTemplateMutation();
  const dirty = title !== template.title || contentHtml !== template.contentHtml || required !== template.required;

  const onSave = () => {
    save.mutate(
      { code: template.code, mall, input: { title, contentHtml, required } },
      {
        onSuccess: () => toast.success('저장되었습니다.'),
        onError: (err) => toast.error(friendlyMessage(err, '동의 문구를 저장하지 못했습니다.')),
      },
    );
  };

  return (
    <SectionCard
      title={COMMON_CONSENT_LABELS[template.code]}
      description={template.updatedAt ? `마지막 저장 ${new Date(template.updatedAt).toLocaleString('ko-KR')}` : '아직 저장한 문구가 없습니다.'}
      data-testid={`consent-template-${template.code}`}
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-[13px]" style={{ color: 'var(--content-default)' }}>
          제목
          <Input
            value={title}
            maxLength={TITLE_MAX}
            disabled={!canEdit}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={mall === 'ko-KR' ? '비우면 쇼핑몰에 내보내지 않습니다' : '비우면 국내몰 문구를 씁니다'}
            data-testid={`consent-template-title-${template.code}`}
          />
        </label>
        <div className="flex flex-col gap-1 text-[13px]" style={{ color: 'var(--content-default)' }}>
          본문
          <RichTextEditor value={contentHtml} onChange={setContentHtml} minHeight={160} aria-label={`${COMMON_CONSENT_LABELS[template.code]} 본문`} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-[13px]" style={{ color: 'var(--content-default)' }}>
            <Checkbox
              checked={required}
              disabled={!canEdit}
              onCheckedChange={(v) => setRequired(v === true)}
              aria-label="필수 동의"
              data-testid={`consent-template-required-${template.code}`}
            />
            필수 동의 (동의해야 주문할 수 있음)
          </label>
          {canEdit ? (
            <Button
              type="button"
              size="sm"
              variant="cta"
              disabled={!dirty || save.isPending}
              onClick={onSave}
              data-testid={`consent-template-save-${template.code}`}
            >
              저장
            </Button>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
};
